import React from 'react';

interface ProfessionalSpreadCardProps {
  data: {
    pairName: string;
    spread: number;
    beta: number;
    correlation: number;
    zScore: number;
    tickDeltaMs: number;
    tick1AgeMs: number;
    tick2AgeMs: number;
    symbol1: string;
    symbol2: string;
    spreadMin: number;
    spreadMax: number;
    spreadStdDev: number;
    quality: {
      level: string;
      border: string;
      emoji: string;
      label: string;
    };
    timestamp?: string;
  };
  isExpanded: boolean;
  onToggle: () => void;
}

const ProfessionalSpreadCard: React.FC<ProfessionalSpreadCardProps> = ({
  data,
  isExpanded,
  onToggle
}) => {
  // Calculate time ago
  const getTimeAgo = (timestamp?: string) => {
    if (!timestamp) return 'N/A';
    const seconds = Math.floor((new Date().getTime() - new Date(timestamp).getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    return `${Math.floor(minutes / 60)}h ago`;
  };

  return (
    <div
      style={{
        backgroundColor: '#1E293B',
        borderRadius: '8px',
        padding: '16px',
        border: '1px solid #334155',
        borderLeft: `4px solid ${data.quality.border}`,
        boxShadow: `0 0 20px ${data.quality.border}15`,
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden'
      }}
      onClick={onToggle}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateX(3px)';
        e.currentTarget.style.boxShadow = `0 4px 16px ${data.quality.border}30`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateX(0)';
        e.currentTarget.style.boxShadow = `0 0 20px ${data.quality.border}15`;
      }}
    >
      {/* Card Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
        <div style={{
          fontSize: '15px',
          fontWeight: '700',
          color: '#E2E8F0',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          letterSpacing: '0.3px'
        }}>
          <span>{data.quality.emoji}</span>
          <span>{data.pairName}</span>
        </div>
        <div style={{
          fontSize: '14px',
          color: '#64748B',
          transition: 'transform 0.3s',
          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
        }}>
          ⬇️
        </div>
      </div>

      {/* Collapsed View - 4 Column Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '16px',
        marginBottom: '10px'
      }}>
        {/* Spread */}
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontSize: '20px',
            fontWeight: '700',
            color: '#E2E8F0',
            fontFamily: '"Courier New", monospace',
            marginBottom: '6px'
          }}>
            {data.spread.toFixed(5)}
          </div>
          <div style={{
            fontSize: '10px',
            fontWeight: '600',
            color: '#64748B',
            textTransform: 'uppercase',
            letterSpacing: '0.6px'
          }}>
            Spread
          </div>
        </div>

        {/* Correlation */}
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontSize: '20px',
            fontWeight: '700',
            color: '#E2E8F0',
            fontFamily: '"Courier New", monospace',
            marginBottom: '6px'
          }}>
            {Math.round(data.correlation * 100)}%
          </div>
          <div style={{
            fontSize: '10px',
            fontWeight: '600',
            color: '#64748B',
            textTransform: 'uppercase',
            letterSpacing: '0.6px'
          }}>
            Corr {data.correlation >= 0.95 ? '✅' : data.correlation >= 0.90 ? '⚠️' : '🚨'}
          </div>
        </div>

        {/* Tick Sync */}
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontSize: '20px',
            fontWeight: '700',
            color: '#E2E8F0',
            fontFamily: '"Courier New", monospace',
            marginBottom: '6px'
          }}>
            {data.tickDeltaMs}ms
          </div>
          <div style={{
            fontSize: '10px',
            fontWeight: '600',
            color: '#64748B',
            textTransform: 'uppercase',
            letterSpacing: '0.6px'
          }}>
            Sync {data.tickDeltaMs <= 200 ? '✅' : data.tickDeltaMs <= 500 ? '⚠️' : '🚨'}
          </div>
        </div>

        {/* Updated */}
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontSize: '20px',
            fontWeight: '700',
            color: '#E2E8F0',
            fontFamily: '"Courier New", monospace',
            marginBottom: '6px'
          }}>
            {getTimeAgo(data.timestamp)}
          </div>
          <div style={{
            fontSize: '10px',
            fontWeight: '600',
            color: '#64748B',
            textTransform: 'uppercase',
            letterSpacing: '0.6px'
          }}>
            Updated
          </div>
        </div>
      </div>

      {/* Z-Score Badge */}
      <div style={{
        display: 'inline-block',
        fontSize: '12px',
        fontWeight: '600',
        padding: '6px 10px',
        borderRadius: '5px',
        marginTop: '10px',
        backgroundColor: Math.abs(data.zScore) > 2
          ? 'rgba(239, 68, 68, 0.2)'
          : Math.abs(data.zScore) > 1
          ? 'rgba(245, 158, 11, 0.2)'
          : 'rgba(16, 185, 129, 0.2)',
        color: Math.abs(data.zScore) > 2
          ? '#EF4444'
          : Math.abs(data.zScore) > 1
          ? '#F59E0B'
          : '#10B981',
        border: `1px solid ${
          Math.abs(data.zScore) > 2
            ? 'rgba(239, 68, 68, 0.3)'
            : Math.abs(data.zScore) > 1
            ? 'rgba(245, 158, 11, 0.3)'
            : 'rgba(16, 185, 129, 0.3)'
        }`
      }}>
        Z-Score: {data.zScore.toFixed(2)}σ {
          Math.abs(data.zScore) > 2
            ? data.zScore > 0 ? '🚨 OVERBOUGHT' : '🚨 OVERSOLD'
            : Math.abs(data.zScore) > 1
            ? '⚠️ Elevated'
            : '✅ Normal'
        }
      </div>

      {/* Expanded View */}
      {isExpanded && (
        <div style={{
          marginTop: '20px',
          borderTop: '1px solid #334155',
          paddingTop: '20px',
          animation: 'fadeIn 0.3s ease'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr',
            gap: '20px',
            marginBottom: '16px'
          }}>
            {/* Left Column: Analytics */}
            <div style={{
              backgroundColor: 'rgba(15, 23, 42, 0.5)',
              borderRadius: '8px',
              padding: '18px',
              border: '1px solid #334155'
            }}>
              <div style={{
                fontSize: '11px',
                fontWeight: '700',
                color: '#94A3B8',
                textTransform: 'uppercase',
                letterSpacing: '0.8px',
                borderBottom: '1px solid #334155',
                paddingBottom: '8px',
                marginBottom: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <span>📊</span> Spread Analytics
              </div>

              {/* Primary Metric */}
              <div style={{
                fontSize: '28px',
                fontWeight: '700',
                color: '#FFFFFF',
                fontFamily: '"Courier New", monospace',
                marginBottom: '10px'
              }}>
                {data.spread.toFixed(5)}
              </div>

              {/* Range Info */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '11px', color: '#94A3B8', fontWeight: '600', marginBottom: '8px' }}>
                  RANGE (50-WINDOW):
                </div>
                <div style={{
                  position: 'relative',
                  height: '10px',
                  backgroundColor: '#0F172A',
                  borderRadius: '5px',
                  overflow: 'hidden',
                  marginBottom: '8px'
                }}>
                  <div style={{
                    position: 'absolute',
                    height: '100%',
                    width: '100%',
                    background: 'linear-gradient(90deg, #3B82F6 0%, #10B981 100%)'
                  }} />
                  <div style={{
                    position: 'absolute',
                    top: '-5px',
                    left: `${((data.spread - data.spreadMin) / (data.spreadMax - data.spreadMin)) * 100}%`,
                    width: '20px',
                    height: '20px',
                    backgroundColor: '#FFFFFF',
                    borderRadius: '50%',
                    border: '3px solid #3B82F6',
                    boxShadow: '0 2px 10px rgba(59, 130, 246, 0.5)',
                    transform: 'translateX(-10px)'
                  }} />
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '11px',
                  color: '#64748B'
                }}>
                  <span>Min: {data.spreadMin.toFixed(5)}</span>
                  <span>Max: {data.spreadMax.toFixed(5)}</span>
                </div>
              </div>

              {/* Volatility */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 0',
                borderTop: '1px solid rgba(51, 65, 85, 0.5)'
              }}>
                <span style={{ fontSize: '13px', color: '#94A3B8', fontWeight: '500' }}>Volatility:</span>
                <span style={{
                  fontSize: '14px',
                  color: data.spreadStdDev < 0.0001 ? '#10B981' : data.spreadStdDev < 0.001 ? '#F59E0B' : '#EF4444',
                  fontWeight: '600',
                  fontFamily: '"Courier New", monospace'
                }}>
                  {data.spreadStdDev.toFixed(5)} {
                    data.spreadStdDev < 0.0001 ? '✅ Low' : data.spreadStdDev < 0.001 ? '⚠️ Moderate' : '🚨 High'
                  }
                </span>
              </div>
            </div>

            {/* Right Column: Quality Metrics */}
            <div style={{
              backgroundColor: 'rgba(15, 23, 42, 0.5)',
              borderRadius: '8px',
              padding: '18px',
              border: '1px solid #334155'
            }}>
              <div style={{
                fontSize: '11px',
                fontWeight: '700',
                color: '#94A3B8',
                textTransform: 'uppercase',
                letterSpacing: '0.8px',
                borderBottom: '1px solid #334155',
                paddingBottom: '8px',
                marginBottom: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <span>{data.quality.level === 'excellent' ? '✅' : data.quality.level === 'warning' ? '⚠️' : '🚨'}</span> Quality Metrics
              </div>

              {/* Correlation Gauge */}
              <div style={{ marginBottom: '18px' }}>
                <div style={{ fontSize: '13px', color: '#94A3B8', marginBottom: '8px' }}>Correlation</div>
                <div style={{
                  fontSize: '22px',
                  fontWeight: '700',
                  color: data.correlation >= 0.95 ? '#10B981' : data.correlation >= 0.90 ? '#F59E0B' : '#EF4444',
                  fontFamily: '"Courier New", monospace'
                }}>
                  {data.correlation.toFixed(4)} ({Math.round(data.correlation * 100)}%) {
                    data.correlation >= 0.95 ? '✅' : data.correlation >= 0.90 ? '⚠️' : '🚨'
                  }
                </div>
                <div style={{
                  width: '100%',
                  height: '14px',
                  backgroundColor: '#0F172A',
                  borderRadius: '7px',
                  overflow: 'hidden',
                  marginTop: '10px'
                }}>
                  <div style={{
                    height: '100%',
                    width: `${data.correlation * 100}%`,
                    background: 'linear-gradient(90deg, #EF4444 0%, #F59E0B 50%, #10B981 100%)',
                    borderRadius: '7px',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              </div>

              {/* Quality Items */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 0',
                borderBottom: '1px solid rgba(51, 65, 85, 0.5)'
              }}>
                <span style={{ fontSize: '13px', color: '#94A3B8', fontWeight: '500' }}>Tick Sync:</span>
                <span style={{
                  fontSize: '14px',
                  color: data.tickDeltaMs <= 200 ? '#10B981' : data.tickDeltaMs <= 500 ? '#F59E0B' : '#EF4444',
                  fontWeight: '600',
                  fontFamily: '"Courier New", monospace'
                }}>
                  {data.tickDeltaMs}ms {data.tickDeltaMs <= 200 ? '✅' : data.tickDeltaMs <= 500 ? '⚠️' : '🚨'}
                </span>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 0',
                borderBottom: '1px solid rgba(51, 65, 85, 0.5)'
              }}>
                <span style={{ fontSize: '13px', color: '#94A3B8', fontWeight: '500' }}>Beta (Hedge):</span>
                <span style={{
                  fontSize: '14px',
                  color: '#E2E8F0',
                  fontWeight: '600',
                  fontFamily: '"Courier New", monospace'
                }}>
                  {data.beta.toFixed(4)}
                </span>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 0'
              }}>
                <span style={{ fontSize: '13px', color: '#94A3B8', fontWeight: '500' }}>Updated:</span>
                <span style={{
                  fontSize: '12px',
                  color: '#E2E8F0',
                  fontWeight: '600',
                  fontFamily: '"Courier New", monospace'
                }}>
                  {getTimeAgo(data.timestamp)}
                </span>
              </div>
            </div>
          </div>

          {/* Tick Freshness */}
          <div style={{
            backgroundColor: 'rgba(15, 23, 42, 0.5)',
            borderRadius: '8px',
            padding: '14px',
            border: '1px solid #334155',
            marginBottom: '16px'
          }}>
            <div style={{
              fontSize: '11px',
              fontWeight: '700',
              color: '#94A3B8',
              textTransform: 'uppercase',
              letterSpacing: '0.8px',
              borderBottom: '1px solid #334155',
              paddingBottom: '8px',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <span>⏱️</span> Tick Freshness
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 14px',
                backgroundColor: 'rgba(15, 23, 42, 0.7)',
                borderRadius: '6px',
                border: '1px solid #334155'
              }}>
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#E2E8F0' }}>
                  {data.symbol1}:
                </span>
                <span style={{ fontSize: '12px', color: '#64748B' }}>
                  {data.tick1AgeMs}ms ago {data.tick1AgeMs <= 1000 ? '✅' : ''}
                </span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 14px',
                backgroundColor: 'rgba(15, 23, 42, 0.7)',
                borderRadius: '6px',
                border: '1px solid #334155'
              }}>
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#E2E8F0' }}>
                  {data.symbol2}:
                </span>
                <span style={{ fontSize: '12px', color: '#64748B' }}>
                  {data.tick2AgeMs}ms ago {data.tick2AgeMs <= 1000 ? '✅' : ''}
                </span>
              </div>
            </div>
          </div>

          {/* Status Footer */}
          <div style={{
            backgroundColor: Math.abs(data.zScore) > 2
              ? 'rgba(239, 68, 68, 0.1)'
              : Math.abs(data.zScore) > 1
              ? 'rgba(245, 158, 11, 0.1)'
              : 'rgba(59, 130, 246, 0.1)',
            border: `1px solid ${
              Math.abs(data.zScore) > 2
                ? 'rgba(239, 68, 68, 0.3)'
                : Math.abs(data.zScore) > 1
                ? 'rgba(245, 158, 11, 0.3)'
                : 'rgba(59, 130, 246, 0.3)'
            }`,
            borderRadius: '6px',
            padding: '14px',
            fontSize: '12px',
            color: '#94A3B8'
          }}>
            {Math.abs(data.zScore) > 2
              ? `🚨 CRITICAL - Spread in extreme range (${data.zScore > 0 ? 'OVERBOUGHT' : 'OVERSOLD'}). Review hedge immediately.`
              : Math.abs(data.zScore) > 1
              ? '⚠️ Status: Spread ${data.zScore > 0 ? "widening" : "narrowing"} detected. Monitor closely.'
              : '💡 Status: Normal trading range. No action required.'
            }
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default ProfessionalSpreadCard;
